import styled from 'styled-components';

import {
  BarChart as ReBarChart,
  XAxis as ReXAxis,
  YAxis as ReYAxis,
  Tooltip as ReTooltip,
  CartesianGrid as ReGrid,
} from 'recharts';

export const Wrapper = styled.div`
  margin: 24px 0 0 0;
  padding: 24px;
  background: #f8f8f8;
  border-radius: 24px;
  flex-shrink: 0;
`;

export const SectionTitle = styled.div`
  font-family: 'Poppins';
  font-weight: 500;
  font-style: normal;
  font-size: 26px;
  line-height: 100%;
  color: #8e8e8e;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    font-size: 22px;
    margin-bottom: 20px;
  }

  @media (max-width: 480px) {
    font-size: 20px;
    margin-bottom: 16px;
  }
`;

export const TopRow = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

export const MetricCard = styled.div`
  text-align: center;
  border-radius: 13px;
  background: #fff;
  flex-shrink: 0;
  width: 185px;
  height: 70px;
  display: flex;
  flex-direction: column;
  justify-content: center; /* Vertically center */
  align-items: flex-start; /* Horizontally align left */

  .metric-value-label {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding-top: 14px;
    padding-bottom: 12px;
    padding-left: 14px;

    .value {
      font-family: 'Poppins';
      font-size: 22px;
      font-weight: bold;
      color: #007ade;
    }

    .label {
      color: #8e8e8e;
      font-family: 'Poppins';
      font-size: 13px;
      font-style: normal;
      font-weight: 400;
    }
  }

  @media (max-width: 768px) {
    width: 100%;
    height: 60px;
  }

  @media (max-width: 480px) {
    height: 55px;
  }
`;

export const FilterRow = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
  align-items: center;
  flex-wrap: wrap;
  justify-content: space-between;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;

    & > * {
      width: 100% !important;
    }
  }
`;

export const ExportButton = styled.button`
  padding: 10px 12px;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  height: 30px;
  font-family: 'Poppins';
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 6px;
  margin-left: auto;
  color: #8e8e8e;
  transition: all 0.2s ease;

  &:hover {
    background: #f5f5f5;
  }

  &:disabled {
    background: #f5f5f5;
    color: #bdbdbd;
    cursor: not-allowed;
    border-color: #e0e0e0;
  }

  &:disabled:not(:hover) {
    background: #f0f0f0;
  }

  @media (max-width: 768px) {
    width: 100%;
    margin-left: 0;
  }
`;

export const ToggleView = styled.div`
  display: flex;
  gap: 24px;
  border-bottom: 1px solid #e0e0e0;
  margin-bottom: 16px;
`;

export const ToggleButton = styled.button`
  background: transparent;
  border: none;
  outline: none;
  cursor: pointer;
  padding: 6px 0;
  font-size: 14px;
  font-weight: ${({ active }) => (active ? 600 : 500)};
  color: ${({ active }) => (active ? '#007ADE' : '#333')};
  display: flex;
  align-items: center;
  gap: 6px;
  border-bottom: 2px solid
    ${({ active }) => (active ? '#007ADE' : 'transparent')};

  &:hover {
    color: #007ade;
  }
`;

export const ChartBox = styled.div`
  background: #fff;
  border: 1px solid #e0e0e0;
  padding: 20px 20px 20px 0;
  height: 500px;
  overflow-y: auto;
  border-radius: 13px;

  @media (max-width: 768px) {
    padding: 16px;
    height: auto;
  }
`;

export const FilterLabel = styled.div`
  font-family: 'Poppins';
  font-size: 14px;
  color: #8e8e8e;
  margin: 24px 0 4px 0;
`;

export const ChartWrapper = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

export const ChartDivider = styled.div`
  height: 1px;
  background: #f0f0f0;
  margin: 20px 0;
`;

export const ChartStats = styled.div`
  display: flex;
  flex-wrap: wrap;
  padding: 8px 0;
  margin-left: 0;
  justify-content: flex-start;
  align-items: center;
  flex: 1;

  .value-label {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-family: 'Poppins';
    margin-left: 40px;

    @media (max-width: 480px) {
      width: 100%;
      justify-content: center;
      margin-left: 0;
    }
  }

  .label {
    font-size: 14px;
    color: #8e8e8e;
  }

  .value {
    font-size: 20px;
    font-weight: bold;
    color: #007ade;
  }
`;

export const StyledBarChart = styled(ReBarChart)`
  font-size: 12px;
`;

export const StyledXAxis = styled(ReXAxis)`
  .recharts-cartesian-axis-tick-value {
    font-size: 12px;
    fill: #333;
    font-family: 'Poppins';
  }
`;

export const StyledYAxis = styled(ReYAxis)`
  .recharts-cartesian-axis-tick-value {
    font-size: 12px;
    fill: #333;
    font-family: 'Poppins';
  }
`;

export const StyledTooltip = styled(ReTooltip)`
  font-size: 12px;

  .recharts-tooltip-label,
  .recharts-tooltip-item {
    font-size: 12px;
  }
`;

export const StyledGrid = styled(ReGrid)``;

export const TableContainer = styled.div`
  overflow-x: auto;
  background: #fff;
  font-family: 'Poppins';
  max-height: 400px;
  overflow-y: auto;
`;

export const TableHeader = styled.div`
  position: sticky;
  top: 0;
  background: #fff;
  z-index: 2;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  padding: 12px 16px;
  font-size: 14px;
  border-bottom: 1px solid #f0f0f0;

  .left-labels {
    display: flex;
    gap: 40px;
    min-width: 200px;

    .table-col-header {
      font-weight: 500;
      color: #8e8e8e;
      min-width: 80px;
      white-space: nowrap;
    }
  }

  .table-stats {
    display: flex;
    gap: 24px;
    flex-wrap: wrap;
    white-space: nowrap;

    .value-label {
      display: flex;
      align-items: center;
      gap: 6px;

      .label {
        font-weight: 500;
        color: #8e8e8e;
      }

      .value {
        font-weight: 600;
        color: #007ade;
        font-size: 16px;
      }
    }
  }

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;

    .left-labels {
      gap: 24px;
    }

    .table-stats {
      justify-content: flex-start;
      width: 100%;
    }
  }
`;

export const TableContent = styled.div`
  width: 100%;
  overflow-x: auto;
  border-bottom: 1px solid #f0f0f0;

  table {
    width: 100%;
    border-collapse: collapse;
    min-width: 300px;

    td {
      padding: 10px 16px;
      font-size: 14px;
      color: #333;
      border-top: 1px solid #f0f0f0;
      white-space: nowrap;
    }

    tr:last-child td {
      border-bottom: none;
    }
  }
`;
